import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, Skeleton, Stack, Typography } from '@mui/material';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import { SalonMember, TaskActivity, TaskStatus, TaskUser, taskApi } from '../../services/taskService';
import {
  ACTIVITY_LABEL, FIELD_LABEL, STATUS_LABEL, formatDateTime, formatDuration, userLabel,
} from './taskShared';
import { SectionCard, EmptyState } from '../../components/ui';
import { tokens } from '../../theme';

interface Props {
  taskId: string;
  /** Lista osob salonu — z niej bierzemy imie i nazwisko, gdy konto ma tylko e-mail. */
  members?: SalonMember[];
}

const PAGE_SIZE = 25;
const COLLAPSED_COUNT = 6;

/**
 * Konta pracownikow czesto nie maja imienia w profilu uzytkownika, a salon zna je
 * z kartoteki zespolu. Uzupelniamy je z listy osob, zeby zamiast e-maila pokazac nazwisko.
 */
export function withMemberName<T extends TaskUser>(user: T, members?: SalonMember[]): T;
export function withMemberName<T extends TaskUser>(user: T | null | undefined, members?: SalonMember[]): T | null | undefined;
export function withMemberName<T extends TaskUser>(user: T | null | undefined, members: SalonMember[] = []): T | null | undefined {
  if (!user || user.firstName || user.lastName) return user;
  const member = members.find((m) => m.userId === user.id);
  if (!member || (!member.firstName && !member.lastName)) return user;
  return { ...user, firstName: member.firstName, lastName: member.lastName };
}

/** Renders "old → new" in the language the salon uses, not raw column values. */
function describeChange(item: TaskActivity): string | null {
  const asStatus = (v?: string | null) =>
    v && STATUS_LABEL[v as TaskStatus] ? STATUS_LABEL[v as TaskStatus] : v;

  if (item.field === 'status') {
    return `${asStatus(item.oldValue) || '—'} → ${asStatus(item.newValue) || '—'}`;
  }
  if (item.type === 'TIMER_STOPPED' || item.type === 'TIMER_PAUSED') {
    const seconds = Number(item.newValue);
    return Number.isFinite(seconds) ? `Zapisano ${formatDuration(seconds)}` : null;
  }
  if (item.type === 'TIME_CORRECTED') {
    return `${item.oldValue || '—'} → ${item.newValue || '—'}`;
  }
  if (item.type === 'DUE_CHANGED') {
    const fmt = (v?: string | null) => (v ? formatDateTime(v) : 'brak terminu');
    return `${fmt(item.oldValue)} → ${fmt(item.newValue)}`;
  }
  if (item.type === 'ATTACHMENT_ADDED' || item.type === 'ATTACHMENT_REMOVED') {
    return item.newValue || item.oldValue || null;
  }
  if (item.type === 'CHECKLIST_CHANGED') {
    if (item.field && item.newValue) return `„${item.field}” — ${item.newValue}`;
    return item.newValue ? `Dodano „${item.newValue}”` : item.oldValue ? `Usunięto „${item.oldValue}”` : null;
  }
  if (item.field && FIELD_LABEL[item.field]) {
    return `${FIELD_LABEL[item.field]}: ${item.oldValue || '—'} → ${item.newValue || '—'}`;
  }
  return null;
}

/** Zdarzenia zmieniajace etap pracy wyrozniamy kolorem kropki na osi. */
const MILESTONES = new Set([
  'CREATED', 'STATUS_CHANGED', 'SUBMITTED_REVIEW', 'SUBMITTED_APPROVAL', 'APPROVED', 'REWORK_REQUESTED',
  'COMPLETED', 'CANCELLED', 'ARCHIVED', 'RESTORED',
]);

export const TaskHistory: React.FC<Props> = ({ taskId, members }) => {
  const [items, setItems] = useState<TaskActivity[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  const load = useCallback(async (targetPage: number) => {
    setLoading(true);
    try {
      const res = await taskApi.activity(taskId, targetPage, PAGE_SIZE);
      setItems((prev) => (targetPage === 1 ? res.data : [...prev, ...res.data]));
      setTotal(res.pagination.total);
      setPage(res.pagination.page);
    } finally {
      setLoading(false);
    }
  }, [taskId]);

  useEffect(() => { load(1); }, [load]);

  const visible = expanded ? items : items.slice(0, COLLAPSED_COUNT);
  const hiddenLoaded = items.length - visible.length;

  return (
    <SectionCard
      title={(
        <>
          Historia zmian
          {total > 0 && (
            <Box component="span" sx={{ ml: 0.75, color: tokens.muted, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
              {total}
            </Box>
          )}
        </>
      )}
    >
      {loading && items.length === 0 ? (
        <Stack gap={2} aria-busy="true" aria-label="Ładowanie historii">
          {[0, 1, 2].map((i) => (
            <Box key={i} sx={{ pl: 3 }}>
              <Skeleton width={`${45 + i * 12}%`} height={20} />
              <Skeleton width="35%" height={16} />
            </Box>
          ))}
        </Stack>
      ) : items.length === 0 ? (
        <EmptyState compact icon={<HistoryOutlinedIcon />} title="Brak wpisów w historii" description="Każda zmiana w zadaniu pojawi się tutaj z autorem i datą." />
      ) : (
        <Box component="ol" sx={{ listStyle: 'none', m: 0, p: 0 }}>
          {visible.map((item, idx) => {
            const change = describeChange(item);
            const last = idx === visible.length - 1;
            const milestone = MILESTONES.has(item.type);
            return (
              <Box component="li" key={item.id} sx={{ position: 'relative', pl: 3, pb: last ? 0 : 2 }}>
                {!last && (
                  <Box aria-hidden sx={{ position: 'absolute', left: 5, top: 18, bottom: 0, width: 2, borderRadius: 1, bgcolor: tokens.line }} />
                )}
                <Box
                  aria-hidden
                  sx={{
                    position: 'absolute',
                    left: 0,
                    top: 5,
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    bgcolor: milestone ? tokens.plumSoft : tokens.paper,
                    border: `2px solid ${milestone ? tokens.plumLight : tokens.lineStrong}`,
                    boxSizing: 'border-box',
                  }}
                />
                <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600, lineHeight: 1.45 }}>
                  {ACTIVITY_LABEL[item.type] || 'Zmiana w zadaniu'}
                </Typography>
                {change && (
                  <Typography sx={{ fontSize: '0.875rem', color: tokens.inkSoft, wordBreak: 'break-word' }}>
                    {change}
                  </Typography>
                )}
                {item.note && (
                  <Typography sx={{ fontSize: '0.875rem', color: tokens.inkSoft, fontStyle: 'italic', wordBreak: 'break-word' }}>
                    {item.note}
                  </Typography>
                )}
                <Typography sx={{ fontSize: '0.8125rem', color: tokens.muted, mt: 0.25, fontVariantNumeric: 'tabular-nums' }}>
                  {userLabel(withMemberName(item.actor, members))} · {formatDateTime(item.createdAt)}
                </Typography>
              </Box>
            );
          })}
        </Box>
      )}

      {(hiddenLoaded > 0 || items.length < total) && (
        <Button
          variant="text"
          size="small"
          sx={{ mt: 1.5, ml: -1.5 }}
          disabled={loading}
          onClick={() => {
            if (hiddenLoaded > 0) setExpanded(true);
            else load(page + 1);
          }}
        >
          {hiddenLoaded > 0 ? `Pokaż całą historię (${total})` : 'Pokaż starsze wpisy'}
        </Button>
      )}
    </SectionCard>
  );
};

export default TaskHistory;
